import { ChatMenuButtonsComponent } from '../event-interface/chat-menu-buttons'
import { ChatMultiProductCarousel } from '../event-interface/chat-multi-product-carousel'
import { MessageButtons } from '../event-interface/message-buttons'
import { MessageImages } from '../event-interface/message-images'
import { type ChunkAttributes, resolveChatCards } from './chat-attachment-cards'

/**
 * The run panel is a narrow column and the button rows are plain flex rows that neither
 * wrap nor shrink, so a wide row was clipped at the panel edge with its last button
 * unreachable. Scroll it sideways instead.
 */
const Scroller = ({ children }: { children: React.ReactNode }) => (
  <div className="overflow-x-auto max-w-full" data-testid="scroller-chat-attachment">
    {children}
  </div>
)

interface ChatAttachmentsProps {
  attributes?: ChunkAttributes
}

/**
 * The cards a chat turn can carry — quick replies, menu buttons, a product
 * carousel, uploaded files — rendered for a finished run. `isLastChunk={false}`
 * is what puts the shared widget components in read-only mode, so nothing here
 * can send a message.
 */
export const ChatAttachments = ({ attributes }: ChatAttachmentsProps) => {
  if (!attributes) return null
  const { files, quickReplies, menu, carousel } = resolveChatCards(attributes)
  if (!files && !quickReplies && !menu && !carousel) return null

  return (
    <div className="flex flex-col gap-2 min-w-0" data-testid="container-chat-attachments">
      {files ? <MessageImages files={files.files} /> : null}
      {quickReplies ? (
        <Scroller>
          <MessageButtons buttons={quickReplies[0].buttons} isLastChunk={false} />
        </Scroller>
      ) : null}
      {menu ? (
        <Scroller>
          <ChatMenuButtonsComponent content={menu} isLastChunk={false} />
        </Scroller>
      ) : null}
      {/* The carousel brings its own horizontal scroller, so it is not wrapped again. */}
      {carousel ? (
        <ChatMultiProductCarousel content={carousel} isLastChunk={false} fullWidth />
      ) : null}
    </div>
  )
}
